import { FC, useEffect, useState } from 'react'
import { ToastContainer } from 'react-toastify'
import { fetchData, fetchFormData, getTranslatedValue, toasterrormsg, toastsuccessmsg, useMenuIdPermissions } from '../../../../reusable'
import { PageTitle } from '../../../../_metronic/layout/core'
import { Tr } from '../../../../_metronic/i18n/Tr'
import { KTIcon, toAbsoluteUrl } from '../../../../_metronic/helpers'
import { FileUploader } from 'react-drag-drop-files'
import { useNavigate } from 'react-router-dom'
import GoogleMapComponent2 from '../../components/GoogleMapComponent2'
import Select from 'react-select'
import { useLang } from '../../../../_metronic/i18n/Metronici18n'
import AutoComplete from '../../components/AddressAutoComplete'
import Swal from 'sweetalert2'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'

interface PostMedia {
    postmediaId: number
    media: string
}

interface PostDetails {
    postId: number
    destinationName: string
    caption: string
    explorecategoryId: number
    countryId: number
    regionId: number
    latitude: number
    longitude: number
    address: string
    media: PostMedia[]
}

interface Category {
    value: number
    label: string
}

interface Country {
    value: number
    label: string
}

interface Region {
    value: number
    label: string
}



const fileTypes = ['JPG', 'JPEG', 'PNG', 'WEBP']

const EditPost: FC = () => {
    const t = Tr()
    const navigate = useNavigate()
    const { urlData, menuIdDelete, menuIdEdit } = useMenuIdPermissions()
    const lang = useLang()

    // Dropdown Data
    const [categories, setCategories] = useState<Category[]>([])
    const [countries, setCountries] = useState<Country[]>([])
    const [regions, setRegions] = useState<Region[]>([])

    // Media States
    const [existingMedia, setExistingMedia] = useState<PostMedia[]>([])
    const [newMediaFiles, setNewMediaFiles] = useState<File[]>([])

    // UI States
    const [isLoading, setIsLoading] = useState(true)
    const [baseUrl, setBaseUrl] = useState('')

    const MAX_FILE_SIZE = 5 * 1024 * 1024
    const MAX_IMAGES = 5
    // Initial Values for Formik
    const initialValues = {
        postId: '',
        destinationName: '',
        caption: '',
        explorecategoryId: 0,
        countryId: 0,
        regionId: 0,
        latitude: 0,
        longitude: 0,
        address: '',
        media: existingMedia,

        // country: '',
        // state: '',
        // city: ''
    }

    // Validation Schema
    const postValidationSchema = Yup.object().shape({
        destinationName: Yup.string()
            .trim()
            .required('Destination name is required')
            .max(200, 'Maximum 200 characters allowed'),
        caption: Yup.string()
            .max(1000, 'Maximum 1000 characters allowed')
            .required('Caption is required')
        ,
        explorecategoryId: Yup.number()
            .min(1, 'Please select a category')
            .required('Category is required'),
        countryId: Yup.number()
            .min(1, 'Please select a country')
            .required('Country is required'),
        regionId: Yup.number()
            .min(1, 'Please select a region')
            .required('Region is required'),
        address: Yup.string()
            .max(500, 'Maximum 500 characters allowed'),
        latitude: Yup.number()
            .required('Latitude is required'),
        longitude: Yup.number()
            .required('Longitude is required'),
        media: Yup.array()
            .of(Yup.mixed())
            .test(
                'has-media',
                'At least one media file is required',
                function (value) {
                    // Access parent component states via closure
                    const hasExistingMedia = existingMedia.length > 0;
                    const hasNewMedia = newMediaFiles.length > 0;

                    // Valid if either existing media OR new media exists
                    return hasExistingMedia || hasNewMedia;
                }
            ),


    })

    // Formik Configuration
    const formik: any = useFormik({
        initialValues,
        validationSchema: postValidationSchema,
        onSubmit: async (values, { setStatus, setSubmitting }) => {

            try {
                // Prepare form data
                const formData = new FormData()
                formData.append('postId', String(urlData.postId))
                formData.append('destinationName', values.destinationName)
                formData.append('caption', values.caption)
                formData.append('exploreCategoryId', String(values.explorecategoryId))
                formData.append('countryId', String(values.countryId))
                formData.append('regionId', String(values.regionId))
                formData.append('latitude', String(values.latitude))
                formData.append('longitude', String(values.longitude))
                formData.append('address', values.address)

                // Add all new media files in one key
                newMediaFiles.forEach(file => {
                    formData.append('media', file)
                })

                // Submit update
                const response = await fetchFormData('admin/explore/post/edit', formData)

                if (response.status == '200') {
                    setSubmitting(false)
                    toastsuccessmsg(response.message || 'Post updated successfully!')

                } else {
                    setSubmitting(false)
                    toasterrormsg(response.message)
                }
            } catch (error) {
                console.error('Error updating post:', error)
                setStatus('Failed to update post')
                setSubmitting(false)
                toasterrormsg('Failed to update post')
            }
        },
    })

    // Fetch post details
    const postDetailsApiCall = async () => {
        setIsLoading(true)
        const param = { postId: urlData.postId }

        try {
            const response = await fetchData('admin/explore/post/detail', param)

            if (response.status == '200') {
                const data = response.data

                // Set Formik values
                formik.setValues({
                    postId: data.postId || '',
                    destinationName: data.destinationName || '',
                    caption: data.caption || '',
                    explorecategoryId: data.explorecategoryId || 0,
                    countryId: data.countryId || 0,
                    regionId: data.regionId || 0,
                    latitude: data.latitude || 0,
                    longitude: data.longitude || 0,
                    address: data.address || '',
                    media: data.media
                    // country: '',
                    // state: '',
                    // city: ''
                })

                setExistingMedia(data.media || [])
                setBaseUrl(response.url || '')

                // Fetch regions if country exists
                if (data.countryId) {
                    regionListApiCall(data.countryId)
                }
            } else {
                toasterrormsg(response.message)
            }
        } catch (error) {
            console.error('Error fetching post details:', error)
            toasterrormsg('Failed to load post details')
        } finally {
            setIsLoading(false)
        }
    }

    // Fetch categories
    const exploreCategoryListApiCall = async () => {
        try {
            const response = await fetchData('admin/explore/category/list', {})
            if (response.status == '200') {
                const data = response.data.list

                let opt = [] as any
                data.map((item: any) => {
                    if (item.roleId != '1') {
                        opt.push({ value: item.explorecategoryId, label: getTranslatedValue(lang, item, "_name") })
                    }
                })
                setCategories(opt || [])
            } else {
                toasterrormsg(response.message)
            }
        } catch (error) {
            console.error('Error fetching categories:', error)
        }
    }

    // Fetch countries
    const countryListApiCall = async () => {
        try {
            const response = await fetchData('vendor/public/countryList', {})
            if (response.status == '200') {
                var opt: any = []
                response.data.map((val: any) => {
                    opt.push({ value: val.countryId, label: val.nicename })
                })
                setCountries(opt)
            } else {
                toasterrormsg(response.message)
            }
        } catch (error) {
            console.error('Error fetching countries:', error)
        }
    }

    // Fetch regions based on country
    const regionListApiCall = async (selectedCountryId: number) => {
        if (!selectedCountryId) return

        try {
            const response = await fetchData('vendor/public/regionList', { countryId: selectedCountryId })
            if (response.status == '200') {
                let opt: any = []
                response.data.map((val: any) => {
                    opt.push({ value: val.regionId, label: getTranslatedValue(lang, val, '_name') })
                })
                setRegions(opt)
            } else {
                toasterrormsg(response.message)
            }
        } catch (error) {
            console.error('Error fetching regions:', error)
        }
    }

    // Handle new file upload


    const handleFileChange = (files: FileList | File) => {
        const fileArray = files instanceof FileList ? Array.from(files) : [files]

        // 1️⃣ Filter by size first
        const sizeValidFiles = fileArray.filter((file: File) => {
            if (file.size > MAX_FILE_SIZE) {
                toasterrormsg(`${file.name} is larger than 5 MB and was not added.`)
                return false
            }
            return true
        })

        // 2️⃣ Enforce max count (take only remaining slots)
        setNewMediaFiles(prev => {
            const remainingSlots = MAX_IMAGES - prev.length

            if (remainingSlots <= 0) {
                toasterrormsg(`You can upload a maximum of ${MAX_IMAGES} images`)
                return prev
            }

            const filesToAdd = sizeValidFiles.slice(0, remainingSlots)

            if (sizeValidFiles.length > remainingSlots) {
                toasterrormsg(
                    `Only ${remainingSlots} more image(s) can be added`
                )
            }

            return [...prev, ...filesToAdd]
        })
    }


    // Remove new file before upload
    const removeNewFile = (index: number) => {
        setNewMediaFiles(prev => prev.filter((_, i) => i !== index))
    }

    // Mark existing media for deletion
    const handleDelete = async (postmediaId: number) => {

        if (existingMedia.length <= 1) {
            // formik.setFieldTouched('media', true, false)

            // formik.setFieldError('media', 'At least one image must remain')
            toasterrormsg('At least one image must remain')
            return
        }
        let swalOptions: any = {
            title: t('Are You Sure? You Want to Delete This Media.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel')
        }

        const result = await Swal.fire(swalOptions)

        if (!result.isConfirmed) return

        const success = await deleteMediaFromServer(postmediaId)

        if (success) {
            setExistingMedia(prev =>
                prev.filter(media => media.postmediaId !== postmediaId)
            )
            toastsuccessmsg('Media deleted successfully!')
        }
    }

    // Delete media from server
    const deleteMediaFromServer = async (postmediaId: number) => {
        try {
            const response = await fetchData(
                'admin/explore/post/media/delete',
                { postmediaId },
                'DELETE'
            )

            if (response.status == '200') {
                return true
            } else {
                toasterrormsg(response.message)
                return false
            }
        } catch (error) {
            console.error('Error deleting media:', error)
            return false
        }
    }

    // Handle location selection from map
    const handleLocationSelect = (locationData: any) => {
        formik.setFieldValue('latitude', locationData.latitude)
        formik.setFieldValue('longitude', locationData.longitude)
        if (locationData.address) {
            formik.setFieldValue('address', locationData.address)
        }
    }

    // Handle country change
    const handleCountryChange = (option: any) => {
        formik.setFieldValue('countryId', option?.value || 0)
        formik.setFieldValue('regionId', 0) // Reset region
        setRegions([]) // Clear regions
        if (option?.value) {
            regionListApiCall(option.value)
        }
    }

    useEffect(() => {
        if (urlData?.postId) {
            postDetailsApiCall()
            exploreCategoryListApiCall()
            countryListApiCall()
        }
    }, [urlData?.postId])

    if (isLoading) {
        return (
            <>
                <PageTitle breadcrumbs={[]}>{t('Edit Post')}</PageTitle>
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </>
        )
    }

    return (
        <>
            <PageTitle breadcrumbs={[]}>{t('Edit Post')}</PageTitle>
            <ToastContainer />

            <div className="card">
                <div className="card-header border-0 pt-6">
                    <h3 className="card-title align-items-start flex-column">
                        <span className="card-label fw-bold fs-3 mb-1">{t('Edit Post Details')}</span>
                        <span className="text-muted mt-1 fw-semibold fs-7">
                            Update post information and media
                        </span>
                    </h3>

                    <div className='d-flex'  >
                        <div className="card-toolbar mx-2">
                            <button
                                type="button"
                                className="btn btn-sm btn-light-danger"
                                onClick={() => navigate(-1)}
                            >
                                <KTIcon iconName="arrow-left" className="fs-3" />
                                {t('Cancel')}
                            </button>
                        </div>
                        {menuIdEdit && <div className="card-toolbar " >
                            <button
                                type="submit"
                                className="btn btn-sm btn-primary"
                                disabled={formik.isSubmitting}
                            >
                                {formik.isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                                        {t('Updating...')}
                                    </>
                                ) : (
                                    <>
                                        <KTIcon iconName="check" className="fs-3" />
                                        {t('Update Post')}
                                    </>
                                )}
                            </button>
                        </div>}

                    </div>
                </div>

                <form onSubmit={formik.handleSubmit} noValidate>
                    <div className="card-body">
                        <div className="row g-5 post-edit-columns-wrapper" >
                            {/* Left Column - Media Upload */}
                            <div className="col-lg-5">
                                <div className="post-edit-media-section">
                                    <h4 className="mb-4">{t('Post Images')}</h4>

                                    {/* Upload Area */}
                                    <div>
                                        <label className="form-label fw-bold">{t('Add More Images')}</label>
                                        <FileUploader
                                            multiple={true}
                                            handleChange={handleFileChange}
                                            name="media"
                                            types={fileTypes}
                                            classes="w-100"
                                        >
                                            <div className="post-edit-upload-area">
                                                <KTIcon iconName="file-up" className="fs-3x text-primary mb-3" />
                                                <div className="fw-bold fs-5 mb-2">
                                                    {t('Click to upload or drag and drop')}
                                                </div>
                                                <div className="text-muted fs-7">
                                                    JPG, JPEG, PNG, WEBP (max. 5MB each)
                                                </div>
                                            </div>
                                        </FileUploader>
                                        {formik.touched.media && formik.errors.media && (
                                            <div className="fv-plugins-message-container">
                                                <span role="alert" className="text-danger">
                                                    {formik.errors.media}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <div className='mt-4' style={{ maxHeight: "145vh", overflowY: "auto" }} >
                                        {/* Existing Media */}
                                        {existingMedia.length > 0 && (
                                            <div className="mb-5 mt-5">
                                                <label className="form-label fw-bold">{t('Current Images')}</label>
                                                <div className="post-edit-media-grid">
                                                    {existingMedia.map((media) => {
                                                        if (!media.postmediaId) return
                                                        return (
                                                            <>
                                                                <div key={media.postmediaId} className="post-edit-media-item">
                                                                    <img
                                                                        src={baseUrl + media.media}
                                                                        alt="Post media"
                                                                        className="post-edit-media-img"
                                                                        onError={(e) => {
                                                                            e.currentTarget.src = toAbsoluteUrl('/media/logos/mainLogo.png')
                                                                        }}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="post-edit-media-delete"
                                                                        onClick={() => handleDelete(media.postmediaId)}
                                                                        title="Delete image"
                                                                    >
                                                                        <KTIcon iconName="trash" className="fs-3 text-white" iconType='solid' />
                                                                    </button>
                                                                </div>
                                                            </>
                                                        )
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* New Media Preview */}
                                        {newMediaFiles.length > 0 && (
                                            <div className="mb-5">
                                                <label className="form-label fw-bold">{t('New Images to Upload')}</label>
                                                <div className="post-edit-media-grid">
                                                    {newMediaFiles.map((file, index) => (
                                                        <div key={index} className="post-edit-media-item">
                                                            <img
                                                                src={URL.createObjectURL(file)}
                                                                alt="New media"
                                                                className="post-edit-media-img"
                                                            />
                                                            <button
                                                                type="button"
                                                                className="post-edit-media-delete"
                                                                onClick={() => removeNewFile(index)}
                                                                title="Remove image"
                                                            >
                                                                <KTIcon iconName="trash" className="fs-3 text-white" />
                                                            </button>
                                                            <div className="post-edit-media-badge">New</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Right Column - Form Fields */}
                            <div className="col-lg-7">
                                <div className="post-edit-form-section">
                                    {/* Destination Name */}
                                    <div className="mb-5">
                                        <label className="form-label required fw-bold">{t('Destination Name')}</label>
                                        <input
                                            type="text"
                                            className={clsx(
                                                'form-control mb-2',
                                                {
                                                    'is-invalid': formik.touched.destinationName && formik.errors.destinationName,
                                                },
                                                {
                                                    'is-valid': formik.touched.destinationName && !formik.errors.destinationName,
                                                }
                                            )}
                                            placeholder="Enter destination name"
                                            {...formik.getFieldProps('destinationName')}
                                        />
                                        {formik.touched.destinationName && formik.errors.destinationName && (
                                            <div className="fv-plugins-message-container">
                                                <span role="alert" className="text-danger">
                                                    {formik.errors.destinationName}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Caption */}
                                    <div className="mb-5">
                                        <label className="form-label fw-bold">{t('Caption')}</label>
                                        <textarea
                                            className={clsx(
                                                'form-control mb-2',
                                                {
                                                    'is-invalid': formik.touched.caption && formik.errors.caption,
                                                },
                                                {
                                                    'is-valid': formik.touched.caption && !formik.errors.caption,
                                                }
                                            )}
                                            rows={4}
                                            placeholder="Write a caption for your post..."
                                            {...formik.getFieldProps('caption')}
                                        />
                                        {formik.touched.caption && formik.errors.caption && (
                                            <div className="fv-plugins-message-container">
                                                <span role="alert" className="text-danger">
                                                    {formik.errors.caption}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Category */}
                                    <div className="mb-5">
                                        <label className="form-label required fw-bold">{t('Category')}</label>
                                        <Select
                                            name="explorecategoryId"
                                            placeholder={t('Select Explore Category')}
                                            isSearchable
                                            options={categories}
                                            value={
                                                categories.find(
                                                    (option) => option.value === formik.values.explorecategoryId
                                                ) || null
                                            }
                                            onChange={(option: any) => {
                                                formik.setFieldValue('explorecategoryId', option?.value || 0)
                                                formik.setFieldTouched('explorecategoryId', true)
                                            }}
                                            onBlur={() => formik.setFieldTouched('explorecategoryId', true)}
                                            styles={{
                                                container: (provided) => ({ ...provided, width: '100%' }),
                                                control: (provided, state) => ({
                                                    ...provided,
                                                    minHeight: 43,
                                                    borderColor: formik.touched.explorecategoryId && formik.errors.explorecategoryId
                                                        ? '#dc3545'
                                                        : formik.touched.explorecategoryId && !formik.errors.explorecategoryId
                                                            ? '#198754'
                                                            : provided.borderColor
                                                }),
                                            }}
                                        />
                                        {formik.touched.explorecategoryId && formik.errors.explorecategoryId && (
                                            <div className="fv-plugins-message-container">
                                                <span role="alert" className="text-danger">
                                                    {formik.errors.explorecategoryId}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Country and Region */}
                                    <div className="row mb-5">
                                        <div className="col-md-6">
                                            <label className="form-label required fw-bold">{t('Country')}</label>
                                            <Select
                                                name="countryId"
                                                placeholder={t('Select Country')}
                                                isSearchable
                                                options={countries}
                                                value={
                                                    countries.find(
                                                        (option) => option.value === formik.values.countryId
                                                    ) || null
                                                }
                                                onChange={(option: any) => {
                                                    handleCountryChange(option)
                                                    formik.setFieldTouched('countryId', true)
                                                }}
                                                onBlur={() => formik.setFieldTouched('countryId', true)}
                                                styles={{
                                                    container: (provided) => ({ ...provided, width: '100%' }),
                                                    control: (provided) => ({
                                                        ...provided,
                                                        minHeight: 43,
                                                        borderColor: formik.touched.countryId && formik.errors.countryId
                                                            ? '#dc3545'
                                                            : formik.touched.countryId && !formik.errors.countryId
                                                                ? '#198754'
                                                                : provided.borderColor
                                                    }),
                                                }}
                                            />
                                            {formik.touched.countryId && formik.errors.countryId && (
                                                <div className="fv-plugins-message-container">
                                                    <span role="alert" className="text-danger">
                                                        {formik.errors.countryId}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label required fw-bold">{t('Region')}</label>
                                            <Select
                                                name="regionId"
                                                placeholder={t('Select Region')}
                                                isSearchable
                                                options={regions}
                                                value={
                                                    regions.find(
                                                        (option) => option.value === formik.values.regionId
                                                    ) || null
                                                }
                                                onChange={(option: any) => {
                                                    formik.setFieldValue('regionId', option?.value || 0)
                                                    formik.setFieldTouched('regionId', true)
                                                }}
                                                onBlur={() => formik.setFieldTouched('regionId', true)}
                                                styles={{
                                                    container: (provided) => ({ ...provided, width: '100%' }),
                                                    control: (provided) => ({
                                                        ...provided,
                                                        minHeight: 43,
                                                        borderColor: formik.touched.regionId && formik.errors.regionId
                                                            ? '#dc3545'
                                                            : formik.touched.regionId && !formik.errors.regionId
                                                                ? '#198754'
                                                                : provided.borderColor
                                                    }),
                                                }}
                                            />
                                            {formik.touched.regionId && formik.errors.regionId && (
                                                <div className="fv-plugins-message-container">
                                                    <span role="alert" className="text-danger">
                                                        {formik.errors.regionId}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Address */}
                                    <div className="mb-5">
                                        <label className="form-label fw-bold">{t('Address')}</label>
                                        <AutoComplete
                                            name='address'
                                            id='address'
                                            placeholder={t('Enter Location')}
                                            value={formik.values.address}
                                            onAddressSelect={(address: any) => {
                                                formik.setFieldValue('address', address)
                                                formik.setFieldTouched('address', true)
                                            }}
                                            longitude={(lng: any) => formik.setFieldValue('longitude', lng)}
                                            latitude={(lat: any) => formik.setFieldValue('latitude', lat)}
                                            country={(cnt: any) => formik.setFieldValue('country', cnt)}
                                            state={(st: any) => formik.setFieldValue('state', st)}
                                            city={(ct: any) => formik.setFieldValue('city', ct)}
                                        />
                                        {formik.touched.address && formik.errors.address && (
                                            <div className="fv-plugins-message-container">
                                                <span role="alert" className="text-danger">
                                                    {formik.errors.address}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Google Map */}
                                    <div className="mb-5">
                                        <label className="form-label fw-bold mb-3">{t('Location on Map')}</label>
                                        <div className="post-edit-map-container">
                                            <GoogleMapComponent2
                                                latitude={formik.values.latitude}
                                                longitude={formik.values.longitude}
                                                onSelectLocation={handleLocationSelect}
                                            />
                                        </div>
                                        <div className="text-muted fs-7 mt-2">
                                            <KTIcon iconName="information-5" className="fs-6 me-1" />
                                            Click on the map to update location
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Card Footer - Submit Button */}
                    <div className="card-footer d-flex justify-content-end py-6 px-9">
                        <button
                            type="button"
                            className="btn btn-sm btn-light-danger me-3"
                            onClick={() => navigate(-1)}
                        >
                            <KTIcon iconName="arrow-left" className="fs-3" />
                            {t('Cancel')}
                        </button>
                       {menuIdEdit && <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={formik.isSubmitting}
                        >
                            {formik.isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                                    {t('Updating...')}
                                </>
                            ) : (
                                <>
                                    <KTIcon iconName="check" className="fs-3" />
                                    {t('Update Post')}
                                </>
                            )}
                        </button>}
                    </div>
                </form>
            </div>
        </>
    )
}

export { EditPost }